import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, errMsg, img } from "../../lib/api";
import { adminField, AdminLabel } from "./AdminPages";
import { tx, useLang } from "../../lib/i18n";
import { PolicyManager } from "./PolicyManager";
import { PageLoading } from "../../components/PageLoading";

const box = "rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-5";
const SOCIALS = ["instagram", "facebook", "x", "tiktok", "youtube", "linkedin", "telegram", "whatsapp"];
const FIELDS = [["name", "Public trading name"], ["legal_name", "Registered legal company name"], ["registration_country", "Country of registration"], ["registration_number", "Business registration number"],
  ["jurisdiction", "Governing law / jurisdiction"], ["tax_info", "Tax information (e.g. VAT/GST number)"], ["tagline", "Tagline"], ["phone", "Support phone"], ["support_email", "Public support email"],
  ["privacy_email", "Privacy contact email"], ["security_email", "Security alert email (wallet changes)"], ["site_url", "Official website address (https://…)"], ["min_age", "Minimum user age"]];

const Field = ({ label, children }) => <label className="block"><AdminLabel>{tx(label)}</AdminLabel>{children}</label>;

function LogoField({ value, onChange }) { useLang();
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/upload", fd); onChange(data.url); toast.success(tx("Logo uploaded")); } catch (er) { toast.error(tx(errMsg(er))); }
  };
  return (
    <Field label="Company logo">
      <div className="flex items-center gap-3">
        {value ? <img src={img(value)} alt="" className="h-12 w-12 rounded-lg border border-[var(--mv-border)] object-contain" data-testid="company-logo-preview" /> : <div className="h-12 w-12 rounded-lg border border-dashed border-[var(--mv-border-2)]" />}
        <label className="mv-btn-outline flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs"><Upload className="h-3.5 w-3.5" />{tx("Upload")}<input type="file" accept="image/*" className="hidden" onChange={up} data-testid="company-logo-upload" /></label>
        {value && <button onClick={() => onChange(null)} className="text-xs text-[var(--mv-dim)] hover:text-red-400" data-testid="company-logo-remove">{tx("Remove")}</button>}
      </div>
    </Field>
  );
}

export default function CompanySettings() { useLang();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  useEffect(() => { api.get("/admin/company").then((r) => setF({ socials: {}, ...r.data })).catch((e) => toast.error(tx(errMsg(e)))); }, []);
  if (!f) return <p className="text-sm text-[var(--mv-dim)]"><PageLoading /></p>;
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || null])), name: f.name || "MIVO", address: f.address || null, logo_url: f.logo_url || null, socials: f.socials };
      const { data } = await api.put("/admin/company", body); setF({ socials: {}, ...data }); setRk(rk + 1); toast.success(tx("Company settings saved — live on the website now"));
    } catch (e) { toast.error(tx(errMsg(e))); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="company-settings">
      <div className={box}>
        <h2 className="text-lg font-semibold">{tx("Company settings")}</h2>
        <p className="mt-1 text-xs text-[var(--mv-dim)]">{tx("Changes appear on the public website immediately — no redeploy needed.")} {tx("Email links currently open")}: <span className="font-mono" data-testid="company-effective-url">{f.effective_site_url}</span>{f.app_env !== "production" && <span> · {tx("(staging always uses its own preview address)")}</span>}</p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {FIELDS.map(([k, l]) => <Field key={k} label={l}><input className={adminField} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`company-${k}`} /></Field>)}
          <LogoField value={f.logo_url} onChange={(v) => set("logo_url", v)} />
          <div className="md:col-span-2"><Field label="Business address"><textarea rows={2} className={adminField} value={f.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="company-address" /></Field></div>
        </div>
      </div>
      <div className={box}>
        <h3 className="font-semibold">{tx("Social media")}</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {SOCIALS.map((k) => <Field key={k} label={k}><input className={adminField} placeholder="https://" value={f.socials[k] || ""} onChange={(e) => set("socials", { ...f.socials, [k]: e.target.value })} data-testid={`company-social-${k}`} /></Field>)}
        </div>
      </div>
      <button disabled={busy} onClick={save} className="mv-btn-primary rounded-xl px-6 py-3 text-sm disabled:opacity-50" data-testid="company-save">{tx(busy ? "Saving…" : "Save company settings")}</button>
      <PolicyManager refreshKey={rk} />
    </div>
  );
}
